import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import {
    buildActivitiesIds,
    buildObservations,
    getActivities,
} from '../../../../utils/shared/instructionUtils';
import ActivitiesWithPagination from '../../../activities/ActivitiesWithPagination';
import Observation from './Observation';
import { OBSERVED_PATTERNS } from '../../../../constants/reading/DemoModeConstants';
import './ObservationSection.scss';

const NO_OBSERVATIONS_MESSAGE = 'No specific error patterns were detected.';

const ObservationSection = ({
    content,
    observations,
    staticActivitiesIds,
    firstName,
    withDescription,
    hideObservationsIfNoneExists,
    className,
    measureList,
    observationActivitiesMapper,
    selectedGrade,
    staffId,
    lrsTrackingInfo,
    activitiesData,
    children,
    groupName,
    studentId,
    onHelpIconClick,
    selectObservation,
    selectPage,
    sessionId,
    lastLocation,
    lrsGroup,
}) => {
    const [selectedObservationIds, setSelectedObservationIds] = useState([]);

    useEffect(() => {
        setSelectedObservationIds([]);
    }, [observations]);

    const handleCheckboxChanged = (isChecked, checkBoxId, name, category, assessment) => {
        setSelectedObservationIds(
            isChecked
                ? selectedObservationIds.concat(checkBoxId)
                : selectedObservationIds.filter(id => id !== checkBoxId),
        );
        selectObservation(isChecked, name, category, assessment, studentId, lrsGroup);
    };

    const sortedObservationsWithContent = buildObservations(
        content,
        observations,
        firstName,
        measureList,
    );

    const sortedMergedActivities = getActivities(
        buildActivitiesIds(
            staticActivitiesIds,
            sortedObservationsWithContent,
            selectedObservationIds,
            observationActivitiesMapper,
            selectedGrade,
        ),
        activitiesData,
    );

    const isObservationsWithContentEmpty = isEmpty(sortedObservationsWithContent);

    return (
        <div
            data-tour-step={OBSERVED_PATTERNS}
            className={`observations-activities-section ${className}`}
        >
            {!(hideObservationsIfNoneExists && isObservationsWithContentEmpty) && (
                <>
                    <div className="title">Observed patterns</div>
                    <div className="observations-section">
                        {isObservationsWithContentEmpty
                            ? NO_OBSERVATIONS_MESSAGE
                            : sortedObservationsWithContent.map(observation => (
                                  <Observation
                                      key={observation.id}
                                      {...observation}
                                      studentId={studentId}
                                      withDescription={withDescription}
                                      handleCheckboxChanged={handleCheckboxChanged}
                                      isSelected={selectedObservationIds.includes(observation.id)}
                                      groupName={groupName}
                                      onHelpIconClick={onHelpIconClick}
                                      lrsGroup={lrsGroup}
                                  />
                              ))}
                    </div>
                </>
            )}
            <ActivitiesWithPagination
                activities={sortedMergedActivities}
                staffId={staffId}
                studentId={studentId}
                lrsTrackingInfo={lrsTrackingInfo}
                selectPage={selectPage}
                sessionId={sessionId}
                lastLocation={lastLocation}
                lrsGroup={lrsGroup}
            >
                {children}
            </ActivitiesWithPagination>
        </div>
    );
};

ObservationSection.defaultProps = {
    content: [],
    observations: [],
    staticActivitiesIds: [],
    firstName: null,
    withDescription: false,
    hideObservationsIfNoneExists: false,
    className: '',
    staffId: null,
    studentId: '',
    lrsTrackingInfo: {},
    activitiesData: [],
    measureList: [],
    observationActivitiesMapper: {},
    selectedGrade: null,
    children: <></>,
    groupName: null,
    onHelpIconClick: () => {},
    selectObservation: () => {},
    selectPage: () => {},
    sessionId: '',
    lastLocation: {},
    lrsGroup: '',
};

ObservationSection.propTypes = {
    content: PropTypes.array,
    observations: PropTypes.array,
    staticActivitiesIds: PropTypes.array,
    firstName: PropTypes.string,
    withDescription: PropTypes.bool,
    hideObservationsIfNoneExists: PropTypes.bool,
    className: PropTypes.string,
    staffId: PropTypes.string,
    studentId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    activitiesData: PropTypes.array,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    selectedGrade: PropTypes.string,
    children: PropTypes.element,
    groupName: PropTypes.string,
    onHelpIconClick: PropTypes.func,
    selectObservation: PropTypes.func,
    selectPage: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    lrsGroup: PropTypes.string,
};

export default ObservationSection;
